


// src/components/ProjectWizard.jsx
import React, { useState, useEffect,useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon,CheckCircleIcon, } from '@heroicons/react/24/outline';
import api from '../utils/api';

const steps = [
  { id: 1, name: 'Basic', description: 'Project basics' },
  { id: 2, name: 'Client', description: 'Client information' },
  { id: 3, name: 'Scope', description: 'Deliverables & scope items' },
  { id: 4, name: 'Milestones', description: 'Project phases' },
  { id: 5, name: 'Team', description: 'Assign team members' },
  { id: 6, name: 'Summary', description: 'Review & create' },
];

export default function ProjectWizard({ open, onClose, onSuccess, project }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [fetchingProject, setFetchingProject] = useState(false);
  // const [teams, setTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
const [showDropdown, setShowDropdown] = useState(false);
  
const [form, setForm] = useState({
    name: '',
    project_type: 'Other',
    description: '',
    start_date: '',
    expected_end_date: '',
    client_name: '',
    client_phone: '',
    client_email: '',
    client_billing_address: '',
    project_budget: '',
    scope_items: [],
    milestones: [],
    team_members: [],
  });

  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      if (project) {
        setFetchingProject(true);
        try {
          const { data } = await api.get(`/projects/${project.id}`);
          const proj = data.project;

          setForm({
            name: proj.name || '',
            project_type: proj.project_type || 'Other',
            description: proj.description || '',
            start_date: proj.start_date || '',
            expected_end_date: proj.expected_end_date || '',
            client_name: proj.client_name || '',
            client_phone: proj.client_phone || '',
            client_email: proj.client_email || '',
            client_billing_address: proj.client_billing_address || '',
            project_budget: proj.project_budget || '',
            scope_items: proj.ScopeItems?.map(item => ({
              id: item.id,
              category: item.category || '',
              name: item.name || '',
              quantity: item.quantity || 1,
              frequency: item.frequency || '',
              acceptance_criteria: item.acceptance_criteria || '',
              due_date: item.due_date || '',
            })) || [],
            milestones: proj.Milestones?.map(m => ({
             id: m.id,
              name: m.name || '',
              sequence_no: m.sequence_no || 1,
              planned_start: m.planned_start || '',
              planned_end: m.planned_end || '',
              approval_required: m.approval_required ?? true,
            })) || [],
            team_members: proj.ProjectTeams?.map(team => ({
              user_id: team.user_id,
              role: team.role || 'Member',
              permission_level: team.permission_level || 'EDIT',
            })) || [],
          });
          setCurrentStep(1);
        } catch (err) {
          console.error('Failed to load project for editing:', err);
          alert('Could not load project details. Please try again.');
        } finally {
          setFetchingProject(false);
        }
      } else {
        setForm({
          name: '',
          project_type: 'Other',
          description: '',
          start_date: '',
          expected_end_date: '',
          client_name: '',
          client_phone: '',
          client_email: '',
          client_billing_address: '',
          project_budget: '',
          scope_items: [],
          milestones: [],
          team_members: [],
        });
        setCurrentStep(1);
      }
    };

    loadData();
  }, [project, open]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get('/users');
        setUsers(data.users || data || []);
      } catch (err) {
        console.error('Failed to load users:', err);
      }
    };
    if (open) fetchUsers();
  }, [open]);
  // Filter users based on search
const filteredUsers = users.filter(user =>
  user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  (user.role?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
);

// Toggle member in/out
const toggleTeamMember = (userId) => {
  setForm(prev => {
    const exists = prev.team_members.some(m => m.user_id === userId);
    let newMembers;

    if (exists) {
      // Remove
      newMembers = prev.team_members.filter(m => m.user_id !== userId);
    } else {
      // Add with default role & permission
      newMembers = [
        ...prev.team_members,
        {
          user_id: userId,
          role: 'Member',
          permission_level: 'EDIT'
        }
      ];
    }

    return { ...prev, team_members: newMembers };
  });
};

// Remove single member (from chip)
const removeTeamMember = (userId) => {
  setForm(prev => ({
    ...prev,
    team_members: prev.team_members.filter(m => m.user_id !== userId)
  }));
};

// Close dropdown when clicking outside (optional but nice)
useEffect(() => {
  const handleClickOutside = (e) => {
    if (!e.target.closest('.relative')) {
      setShowDropdown(false);
    }
  };
  document.addEventListener('click', handleClickOutside);
  return () => document.removeEventListener('click', handleClickOutside);
}, []);


//   useEffect(() => {
//   const fetchData = async () => {
//     try {
//       const [usersRes, teamsRes] = await Promise.all([
//         api.get('/users'),
//         api.get('/teams')   // ← make sure you have this endpoint
//       ]);
//       setUsers(usersRes.data.users || []);
//       setTeams(teamsRes.data.teams || []); // add state: const [teams, setTeams] = useState([]);
//     } catch (err) {
//       console.error('Failed to load data:', err);
//     }
//   };
//   if (open) fetchData();
// }, [open]);

  const goNext = () => {
    if (currentStep === 1 && !form.name.trim()) {
      alert('Project name is required');
      return;
    }
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  
const handleSave = async () => {
  setLoading(true);
  try {
    // Prepare main project payload
    const payload = {
      name: form.name.trim(),
      project_type: form.project_type,
      description: form.description?.trim() || null,
      start_date: form.start_date || null,
      expected_end_date: form.expected_end_date || null,
      client_name: form.client_name?.trim() || null,
      client_phone: form.client_phone?.trim() || null,
      client_email: form.client_email?.trim() || null,
      client_billing_address: form.client_billing_address?.trim() || null,
      project_budget: form.project_budget || null,
      team_members: form.team_members,
    };

    let projectId;

    if (project) {
      // ── EDIT EXISTING PROJECT ────────────────────────────────────────
      const { data } = await api.put(`/projects/${project.id}`, payload);
      projectId = project.id;

      // Only create NEW milestones (those without an 'id' field)
      const newMilestones = form.milestones.filter(m => !m.id);

      if (newMilestones.length > 0) {
        await Promise.all(
          newMilestones.map(async (m) => {
            await api.post(`/projects/${projectId}/milestones`, {
              name: m.name.trim(),
              sequence_no: Number(m.sequence_no) || 1,
              planned_start: m.planned_start || null,
              planned_end: m.planned_end || null,
              approval_required: m.approval_required ?? true,
            });
          })
        );
      }

      // Existing milestones (with id) are NOT recreated → no duplication
    } else {
      // ── CREATE NEW PROJECT ───────────────────────────────────────────
      const { data } = await api.post('/projects', payload);
      projectId = data.project.id;

      // Create ALL milestones (all are new)
      if (form.milestones.length > 0) {
        await Promise.all(
          form.milestones.map(async (m) => {
            await api.post(`/projects/${projectId}/milestones`, {
              name: m.name.trim(),
              sequence_no: Number(m.sequence_no) || 1,
              planned_start: m.planned_start || null,
              planned_end: m.planned_end || null,
              approval_required: m.approval_required ?? true,
            });
          })
        );
      }
    }

    // Scope items – create new ones (your current logic – no change needed)
    // if (form.scope_items.length > 0) {
    //   await Promise.all(
    //     form.scope_items.map(item =>
    //       api.post(`/projects/${projectId}/scope-items`, item)
    //     )
    //   );
    // }// Scope items – only create NEW ones during edit
if (form.scope_items.length > 0) {
  if (project) {
    // EDIT mode: only create items without id (newly added)
    const newScopeItems = form.scope_items.filter(item => !item.id);
    
    if (newScopeItems.length > 0) {
      await Promise.all(
        newScopeItems.map(item =>
          api.post(`/projects/${projectId}/scope-items`, {
            category: item.category || null,
            name: item.name.trim(),
            quantity: Number(item.quantity) || 1,
            frequency: item.frequency || null,
            acceptance_criteria: item.acceptance_criteria || null,
            due_date: item.due_date || null,
          })
        )
      );
    }
  } else {
    // CREATE mode: create all (all are new)
    await Promise.all(
      form.scope_items.map(item =>
        api.post(`/projects/${projectId}/scope-items`, {
          category: item.category || null,
          name: item.name.trim(),
          quantity: Number(item.quantity) || 1,
          frequency: item.frequency || null,
          acceptance_criteria: item.acceptance_criteria || null,
          due_date: item.due_date || null,
        })
      )
    );
  }
}

    // Success – refresh parent list/details
    onSuccess();
    onClose();
  } catch (err) {
    console.error("Save project error:", err);
    alert(err.response?.data?.message || 'Failed to save project. Check console for details.');
  } finally {
    setLoading(false);
  }
};
//   const handleSave = async () => {
//     setLoading(true);
//     try {
//       const payload = {
//         name: form.name.trim(),
//         project_type: form.project_type,
//         description: form.description?.trim() || null,
//         start_date: form.start_date || null,
//         expected_end_date: form.expected_end_date || null,
//         client_name: form.client_name?.trim() || null,
//         client_phone: form.client_phone?.trim() || null,
//         client_email: form.client_email?.trim() || null,
//         client_billing_address: form.client_billing_address?.trim() || null,
//         project_budget: form.project_budget || null,
//   //       assigned_user_id: form.assigned_user_id || null,     // ← NEW
//   // assigned_team_id: form.assigned_team_id || null,
//         team_members: form.team_members,
//       };

//       let response;
//       let projectId;
//       if (project) {
//         response = await api.put(`/projects/${project.id}`, payload);
//         projectId = project.id;
//       } else {
//         response = await api.post('/projects', payload);
//         projectId = response.data.project.id;
//       }

//       if (form.scope_items.length > 0) {
//         await Promise.all(
//           form.scope_items.map(item =>
//             api.post(`/projects/${projectId}/scope-items`, item)
//           )
//         );
//       }

//       // if (form.milestones.length > 0) {
//       //   await Promise.all(
//       //     form.milestones.map(milestone =>
//       //       api.post(`/projects/${projectId}/milestones`, milestone)
//       //     )
//       //   );
//       // }
//       if (!project) {  // only on create
//   if (form.milestones.length > 0) {
//     await Promise.all(
//       form.milestones.map(m =>
//         api.post(`/projects/${projectId}/milestones`, m)
//       )
//     );
//   }
// }

//       onSuccess();
//       onClose();
//     } catch (err) {
//       alert(err.response?.data?.message || 'Failed to save project');
//     } finally {
//       setLoading(false);
//     }
//   };
const dropdownRef = useRef(null);

// Auto-close dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  // Only listen when dropdown is open
  if (showDropdown) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  // Cleanup
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [showDropdown]);   // Re-run when showDropdown changes
  const addScopeItem = () => {
    setForm(prev => ({
      ...prev,
      scope_items: [
        ...prev.scope_items,
        { category: '', name: '', quantity: 1, frequency: '', acceptance_criteria: '', due_date: '' }
      ]
    }));
  };

  const updateScopeItem = (index, field, value) => {
    setForm(prev => {
      const items = [...prev.scope_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, scope_items: items };
    });
  };

  const removeScopeItem = index => {
    setForm(prev => ({
      ...prev,
      scope_items: prev.scope_items.filter((_, i) => i !== index)
    }));
  };

  const addMilestone = () => {
    setForm(prev => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        { name: '', sequence_no: prev.milestones.length + 1, planned_start: '', planned_end: '', approval_required: true }
      ]
    }));
  };
const handleCancel = () => {
  if (Object.values(form).some(v => v !== '' && v !== null && (!Array.isArray(v) || v.length > 0))) {
    if (!window.confirm("You have entered some data. Are you sure you want to cancel and discard everything?")) {
      return;
    }
  }
  onClose();
};
  const updateMilestone = (index, field, value) => {
    setForm(prev => {
      const items = [...prev.milestones];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, milestones: items };
    });
  };

  const removeMilestone = index => {
    setForm(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }));
  };

  const handleTeamChange = (e) => {
    const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
    const members = selectedIds.map(id => ({
      user_id: parseInt(id, 10),
      role: 'Member',
      permission_level: 'EDIT'
    }));
    setForm(prev => ({ ...prev, team_members: members }));
  };

  return (
    <Transition show={open} as={React.Fragment}>
      {/* <Dialog onClose={onClose} className="relative z-50"> */}
      <Dialog
    onClose={() => {}}           // ← Prevents any automatic close
    static                       // ← Headless UI prop: blocks backdrop click + Esc
    open={open}
    className="relative z-50"
  >
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 text-left align-middle shadow-2xl transition-all">
                <div className="px-8 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-6">
                    <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-white">
                      {project ? 'Edit Project' : 'Create New Project'}
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <nav aria-label="Progress">
                    <ol role="list" className="flex items-center justify-between">
                      {steps.map((step, stepIdx) => (
                        <li key={step.id} className="relative flex-1">
                          {stepIdx < currentStep - 1 ? (
                            <div className="absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 bg-orange-600" />
                          ) : stepIdx === currentStep - 1 ? (
                            <div className="absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 bg-orange-600" />
                          ) : (
                            <div className="absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 bg-gray-300 dark:bg-gray-600" />
                          )}

                          <div
                            className={`relative flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                              stepIdx + 1 <= currentStep
                                ? 'border-orange-600 bg-orange-600 text-white'
                                : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
                            }`}
                          >
                            <span className="text-sm font-medium">{step.id}</span>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </nav>
                </div>

                <div className="px-8 py-10 min-h-[500px]">
                  {fetchingProject ? (
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                    </div>
                  ) : (
                    <>
                      {currentStep === 1 && (
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Basic Information</h3>
                            <p className="mt-2 text-gray-600 dark:text-gray-400">
                              Let's start with the project basics
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"> 
                          
                                Project Name *
                              </label>
                              <input
                                type="text"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                className="block w-full rounded-lg border-gray-800 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                                placeholder="Enter project name"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Project Type
                              </label>
                              <select
                                value={form.project_type}
                                onChange={e => setForm({ ...form, project_type: e.target.value })}
                                className="block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                              >
                                <option value="Other">Select project type</option>
                                <option value="Digital Marketing">Digital Marketing</option>
                                <option value="Website">Website</option>
                                <option value="SEO">SEO</option>
                                <option value="Branding">Branding</option>
                                <option value="Custom">Custom</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Description
                            </label>
                            <textarea
                              rows={4}
                              value={form.description}
                              onChange={e => setForm({ ...form, description: e.target.value })}
                              className="block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                              placeholder="Brief description of the project requirements and vision"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Start Date
                              </label>
                              <input
                                type="date"
                                value={form.start_date}
                                onChange={e => setForm({ ...form, start_date: e.target.value })}
                                className="block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Expected End Date
                              </label>
                              <input
                                type="date"
                                value={form.expected_end_date}
                                onChange={e => setForm({ ...form, expected_end_date: e.target.value })}
                                className="block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {currentStep === 2 && (
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Client Information</h3>
                            <p className="mt-2 text-gray-600 dark:text-gray-400">
                              Tell us about your client
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Client Name
                              </label>
                              <input
                                type="text"
                                value={form.client_name}
                                onChange={e => setForm({ ...form, client_name: e.target.value })}
                                className="block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                                placeholder="Anderson"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Phone
                              </label>
                              <input
                                type="tel"
                                value={form.client_phone}
                                onChange={e => setForm({ ...form, client_phone: e.target.value })}
                                className="block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                                placeholder="+91"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Email
                              </label>
                              <input
                                type="email"
                                value={form.client_email}
                                onChange={e => setForm({ ...form, client_email: e.target.value })}
                                className="block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                                placeholder="client@example.com"
                              />
                            </div>

                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Billing Address
                              </label>
                              <textarea
                                value={form.client_billing_address}
                                onChange={e => setForm({ ...form, client_billing_address: e.target.value })}
                                rows={3}
                                className="block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2.5"
                                placeholder="Client's billing address"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Project Budget
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <span className="text-gray-500 dark:text-gray-400">₹</span>
                                </div>
                                <input
                                  type="number"
                                  value={form.project_budget}
                                  onChange={e => setForm({ ...form, project_budget: e.target.value })}
                                  className="block w-full pl-8 pr-4 py-2.5 border rounded-lg dark:bg-gray-800 dark:border-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                                  placeholder="25,00,000"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {currentStep === 3 && (
                        <div className="space-y-6">
                          <div className="flex justify-between items-center">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Project Scope</h3>
                            <button
                              type="button"
                              onClick={addScopeItem}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-slate-800 hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <PlusIcon className="w-5 h-5 mr-2" />
                              Add Scope Item
                            </button>
                          </div>

                          {form.scope_items.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <p className="text-gray-500 dark:text-gray-400">No scope items added yet. Click "Add Scope Item" to begin.</p>
                            </div>
                          ) : (
                            <div className="space-y-6">
                              {form.scope_items.map((item, index) => (
                                <div key={index} className="relative bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                  <button
                                    type="button"
                                    onClick={() => removeScopeItem(index)}
                                    className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Category
                                      </label>
                                      <input
                                        type="text"
                                        value={item.category}
                                        onChange={e => updateScopeItem(index, 'category', e.target.value)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                        placeholder="SMM / Website / SEO"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Service Name *
                                      </label>
                                      <input
                                        type="text"
                                        value={item.name}
                                        onChange={e => updateScopeItem(index, 'name', e.target.value)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                        placeholder="Create 12 Instagram Posts"
                                        required
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                       Level
                                      </label>
                                      <input
                                        type="number"
                                        value={item.quantity}
                                        onChange={e => updateScopeItem(index, 'quantity', Number(e.target.value))}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                        min="1"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Schedule
                                      </label>
                                      <select
                                        value={item.frequency}
                                        onChange={e => updateScopeItem(index, 'frequency', e.target.value)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                      >
                                        <option value="">One-time</option>
                                        <option value="Weekly">Weekly</option>
                                        <option value="Monthly">Monthly</option>
                                        <option value="Quarterly">Quarterly</option>
                                      </select>
                                    </div>

                                    <div className="md:col-span-2">
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Acceptance Criteria
                                      </label>
                                      <textarea
                                        value={item.acceptance_criteria}
                                        onChange={e => updateScopeItem(index, 'acceptance_criteria', e.target.value)}
                                        rows={3}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                        placeholder="What needs to be delivered and how it will be accepted"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Due Date
                                      </label>
                                      <input
                                        type="date"
                                        value={item.due_date}
                                        onChange={e => updateScopeItem(index, 'due_date', e.target.value)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {currentStep === 4 && (
                        <div className="space-y-6">
                          <div className="flex justify-between items-center">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Milestones</h3>
                            <button
                              type="button"
                              onClick={addMilestone}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-slate-800 hover:bg-orange-500"
                            >
                              <PlusIcon className="w-5 h-5 mr-2" />
                              Add Milestone
                            </button>
                          </div>

                          {form.milestones.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <p className="text-gray-500 dark:text-gray-400">No milestones added yet</p>
                            </div>
                          ) : (
                            <div className="space-y-6">
                              {form.milestones.map((milestone, index) => (
                                <div key={index} className="relative bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                  {/* <button
                                    type="button"
                                    onClick={() => removeMilestone(index)}
                                    className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button> */}

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                      <label className="block text-sm font-medium mb-1">Milestone Name *</label>
                                      <input
                                        type="text"
                                        value={milestone.name}
                                        onChange={e => updateMilestone(index, 'name', e.target.value)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                        placeholder="Design Phase "
                                        required
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium mb-1">Phase No *</label>
                                      <input
                                        type="number"
                                        value={milestone.sequence_no}
                                        onChange={e => updateMilestone(index, 'sequence_no', parseInt(e.target.value) || 1)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                        min="1"
                                      />
                                    </div>

                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={milestone.approval_required}
                                        onChange={e => updateMilestone(index, 'approval_required', e.target.checked)}
                                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                      />
                                      <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                        Approval Required
                                      </label>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium mb-1">Planned Start</label>
                                      <input
                                        type="date"
                                        value={milestone.planned_start}
                                        onChange={e => updateMilestone(index, 'planned_start', e.target.value)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium mb-1">Planned End</label>
                                      <input
                                        type="date"
                                        value={milestone.planned_end}
                                        onChange={e => updateMilestone(index, 'planned_end', e.target.value)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm px-4 py-2"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* {currentStep === 5 && (
  <div className="space-y-6">
    <div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Assign Team Members</h3>
      <p className="mt-2 text-gray-600 dark:text-gray-400">
        Search and select team members who will work on this project
      </p>
    </div>

    <div className="relative">
      <input
        type="text"
        placeholder="Search team members..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:border-orange-500 focus:ring-orange-500 shadow-sm"
        onFocus={() => setShowDropdown(true)}
      />

 
      <div className="mt-3 flex flex-wrap gap-2">
        {form.team_members.map((member) => {
          const user = users.find(u => u.id === member.user_id);
          if (!user) return null;
          return (
            <div
              key={member.user_id}
              className="flex items-center gap-2 bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 px-3 py-1 rounded-full text-sm"
            >
              <span>{user.name} {user.role?.name ? `(${user.role.name})` : ''}</span>
              <button
                type="button"
                onClick={() => removeTeamMember(member.user_id)}
                className="text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

    
      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="px-4 py-3 text-gray-500 dark:text-gray-400">
              No team members found
            </div>
          ) : (
            filteredUsers.map(user => {
              const isSelected = form.team_members.some(m => m.user_id === user.id);
              return (
                <div
                  key={user.id}
                  onClick={() => toggleTeamMember(user.id)}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 ${
                    isSelected ? 'bg-orange-50 dark:bg-orange-900/20' : ''
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-300 font-medium">
                    {user.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {user.role?.name || 'Member'}
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircleIcon className="h-5 w-5 text-green-600 ml-auto" />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>

   
    <input type="hidden" name="team_members" value={JSON.stringify(form.team_members)} />
  </div>
)} */}
{currentStep === 5 && (
  <div className="space-y-6">
    <div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Assign Team Members</h3>
      <p className="mt-2 text-gray-600 dark:text-gray-400">
        Search and select team members who will work on this project
      </p>
    </div>

    {/* Dropdown + Search container – we use a ref here for outside click */}
    <div className="relative" ref={dropdownRef}>
      <input
        type="text"
        placeholder="Search team members..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:border-orange-500 focus:ring-orange-500 shadow-sm"
        onFocus={() => setShowDropdown(true)}           // open on focus
      />

      {/* Selected Chips */}
      <div className="mt-3 flex flex-wrap gap-2">
        {form.team_members.map((member) => {
          const user = users.find(u => u.id === member.user_id);
          if (!user) return null;
          return (
            <div
              key={member.user_id}
              className="flex items-center gap-2 bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 px-3 py-1 rounded-full text-sm"
            >
              <span>{user.name} {user.role?.name ? `(${user.role.name})` : ''}</span>
              <button
                type="button"
                onClick={() => removeTeamMember(member.user_id)}
                className="text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Dropdown List – only shown when showDropdown is true */}
      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="px-4 py-3 text-gray-500 dark:text-gray-400">
              No team members found
            </div>
          ) : (
            filteredUsers.map(user => {
              const isSelected = form.team_members.some(m => m.user_id === user.id);
              return (
                <div
                  key={user.id}
                  onClick={() => {
                    toggleTeamMember(user.id);
                    // ── IMPORTANT: Close dropdown immediately after selection ──
                    setShowDropdown(false);
                    // Optional: clear search term after selection
                    setSearchTerm('');
                  }}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 ${
                    isSelected ? 'bg-orange-50 dark:bg-orange-900/20' : ''
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-300 font-medium">
                    {user.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {user.role?.name || 'Member'}
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircleIcon className="h-5 w-5 text-green-600 ml-auto" />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>

    {/* Hidden input for form submission (if needed) */}
    <input type="hidden" name="team_members" value={JSON.stringify(form.team_members)} />
  </div>
)}

                      {currentStep === 6 && (
                        <div className="space-y-8">
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Review & Save Project</h3>

                          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                              <div className="col-span-2">
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Project Name</dt>
                                <dd className="mt-1 text-base font-medium text-gray-900 dark:text-white">{form.name || '—'}</dd>
                              </div>

                              <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</dt>
                                <dd className="mt-1 text-gray-900 dark:text-white">{form.project_type}</dd>
                              </div>

                              <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Timeline</dt>
                                <dd className="mt-1 text-gray-900 dark:text-white">
                                  {form.start_date || '—'} → {form.expected_end_date || '—'}
                                </dd>
                              </div>

                              <div className="col-span-2">
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</dt>
                                <dd className="mt-1 text-gray-900 dark:text-white">{form.description || 'No description'}</dd>
                              </div>

                              <div className="col-span-2 border-t border-gray-200 dark:border-gray-700 pt-6">
                                <dt className="text-base font-medium text-gray-900 dark:text-white">Client Details</dt>
                                <dd className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div>
                                    <span className="block text-sm text-gray-500 dark:text-gray-400">Name</span>
                                    <span className="text-gray-900 dark:text-white">{form.client_name || '—'}</span>
                                  </div>
                                  <div>
                                    <span className="block text-sm text-gray-500 dark:text-gray-400">Phone</span>
                                    <span className="text-gray-900 dark:text-white">{form.client_phone || '—'}</span>
                                  </div>
                                  <div>
                                    <span className="block text-sm text-gray-500 dark:text-gray-400">Email</span>
                                    <span className="text-gray-900 dark:text-white">{form.client_email || '—'}</span>
                                  </div>
                                  <div className="md:col-span-2">
                                    <span className="block text-sm text-gray-500 dark:text-gray-400">Billing Address</span>
                                    <span className="text-gray-900 dark:text-white">{form.client_billing_address || '—'}</span>
                                  </div>
                                  <div>
                                    <span className="block text-sm text-gray-500 dark:text-gray-400">Budget</span>
                                    <span className="text-gray-900 dark:text-white">
                                      {form.project_budget ? `₹${Number(form.project_budget).toLocaleString('en-IN')}` : '—'}
                                    </span>
                                  </div>
                                </dd>
                              </div>

                              <div className="col-span-2 border-t border-gray-200 dark:border-gray-700 pt-6">
                                <dt className="text-base font-medium text-gray-900 dark:text-white">Scope Items ({form.scope_items.length})</dt>
                                <dd className="mt-4">
                                  {form.scope_items.length === 0 ? (
                                    <p className="text-gray-500">None added</p>
                                  ) : (
                                    <ul className="space-y-2">
                                      {form.scope_items.map((item, i) => (
                                        <li key={i} className="flex justify-between text-gray-900 dark:text-white">
                                          <span>{item.name} ({item.category || 'Uncategorized'})</span>
                                          <span className="text-gray-500">Qty: {item.quantity}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </dd>
                              </div>

                              <div className="col-span-2 border-t border-gray-200 dark:border-gray-700 pt-6">
                                <dt className="text-base font-medium text-gray-900 dark:text-white">Milestones ({form.milestones.length})</dt>
                                <dd className="mt-4">
                                  {form.milestones.length === 0 ? (
                                    <p className="text-gray-500">None added</p>
                                  ) : (
                                    <ol className="list-decimal pl-5 space-y-2">
                                      {form.milestones.map((m, i) => (
                                        <li key={i} className="text-gray-900 dark:text-white">
                                          {m.name} (Seq {m.sequence_no})
                                        </li>
                                      ))}
                                    </ol>
                                  )}
                                </dd>
                              </div>

                              <div className="col-span-2 border-t border-gray-200 dark:border-gray-700 pt-6">
                                <dt className="text-base font-medium text-gray-900 dark:text-white">Team Members ({form.team_members.length})</dt>
                                <dd className="mt-4">
                                  {form.team_members.length === 0 ? (
                                    <p className="text-gray-500">None assigned</p>
                                  ) : (
                                    <ul className="space-y-2">
                                      {form.team_members.map((m, i) => {
                                        const user = users.find(u => u.id === m.user_id);
                                        return (
                                          <li key={i} className="text-gray-900 dark:text-white">
                                            {user?.name || `User ${m.user_id}`}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="px-8 py-6 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800">
                  <button
                    onClick={handleCancel}
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  >
                    Cancel
                  </button>

                  <div className="flex items-center gap-4">
                    {currentStep > 1 && (
                      <button
                        onClick={goBack}
                        className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                      >
                        Back
                      </button>
                    )}

                    {currentStep === steps.length ? (
                      <button
                        onClick={handleSave}
                        disabled={loading || !form.name.trim()}
                        className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition flex items-center gap-2"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path fill="currentColor" d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z" />
                            </svg>
                            {project ? 'Updating...' : 'Creating...'}
                          </>
                        ) : (
                          project ? 'Update Project' : 'Create Project'
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={goNext}
                        disabled={currentStep === 1 && !form.name.trim()}
                        className="px-8 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition"
                      >
                        Next
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}